import {
  FluidElementState,
  FluidKeyedObject,
  FluidTheme,
} from '@lmig/fluid-core';
import { readTask } from '@stencil/core';
import { BehaviorSubject, interval } from 'rxjs';
import { filter, map, take, takeWhile } from 'rxjs/operators';

// ================================================================ //
// -- FLUID Element Registry

/**
 * Allows FLUID components to be aware of each other's positions within
 * the DOM, as well as send data among themselves. NOTE: components
 * should only be added to the "registry" if their DOM positions need
 * to be tracked - in the future, a separate "dataRegistry" will be
 * added for exclusively sending data between components without
 * tracking/calculating DOM position.
 */
class FluidElementRegistry {
  // FLUID Element Registry object
  public registry: FluidKeyedObject<FluidElementState> = {};

  // Element Registry BehaviorSubject
  public registry$: BehaviorSubject<FluidKeyedObject<FluidElementState>> =
    new BehaviorSubject<FluidKeyedObject<FluidElementState>>(this.registry);

  // Public reference to Theme Provider element
  public themeProviderElement: HTMLFluidThemeProviderElement;

  // Theme Provider BehaviorSubject
  public themeProviderState$: BehaviorSubject<FluidElementState> =
    new BehaviorSubject<FluidElementState>(undefined);

  /**
   * Waits for an entry to be added to the registry - useful if a micro-ui is created
   * with something that would typically attach to the registry
   * @param entryName
   * @param timeout
   */
  waitForEntry = (entryName: string, timeout: number) => {
    return interval(100) // Check every 100 milliseconds
      .pipe(
        takeWhile((elapsedTime) => elapsedTime < timeout),
        filter(() => this.registry[entryName] !== undefined),
        map(() => this.registry[entryName]),
        take(1)
      );
  };

  /**
   * Adds component entry to Element Registry & triggers updates to
   * all other Element Registry entries' DOM positions.
   * @param name - name for Element Registry entry (should be component name)
   * @param element - the component's "host" element
   * @param dataStore - optional key/value pairs of additional data
   */
  addElement = (
    name: string,
    element: HTMLElement,
    dataStore?: FluidKeyedObject<any>
  ): void => {
    if (this.registry[name]) return;
    this.registry[name] = { element, dataStore };
    this.updateRegistry(true);
  };

  /**
   * Removes component entry from Element Registry & triggers updates to
   * other Element Registry entries' DOM positions.
   * @param name - the name of the entry to remove
   * @param element
   */
  removeElement = (name: string, element?: HTMLElement): void => {
    if (
      !this.registry[name] ||
      (!!element && this.registry[name].element !== element)
    )
      return;
    delete this.registry[name];
    this.updateRegistry();
  };

  /**
   * Updates a component entry's "dataStore" property WITHOUT triggering
   * updates to other entries' DOM positions. Merges new "dataStore"
   * object with existing "dataStore" so that updates are applied selectively.
   * @param name - name of entry to update
   * @param dataStore - new "dataStore" object
   * @param updateRegistry - pushes update to Subscribers when true
   */
  updateElementDataStore = (
    name: string,
    dataStore: FluidKeyedObject<any>,
    updateRegistry?: boolean
  ): void => {
    if (!this.registry[name]) return;

    this.registry[name].dataStore = {
      ...this.registry[name].dataStore,
      ...dataStore,
    };

    // Push update to Subscribers without recalculating DOM positions
    updateRegistry && this.registry$.next(this.registry);
  };

  /**
   * Updates ALL Element Registry entries' DOM positions, then pushes
   * changes to Subscribers.
   * @param immediate - when false, update is scheduled as DOM-read task to prevent "layout thrashing"
   */
  updateRegistry = (immediate = false): void => {
    if (!Object.keys(this.registry).length) return;

    if (immediate) {
      updateRegistry(this.registry);
      this.registry$.next(this.registry);
    } else {
      // See https://stenciljs.com/docs/api for readTask() info
      readTask(() => {
        updateRegistry(this.registry);
        this.registry$.next(this.registry);
      });
    }
  };

  /**
   * Sets public "themeProviderElement" & "themeProviderState$"
   * properties to allow all other FLUID components to access
   * Theme Provider's "host" element & current theme. Called by
   * Theme Provider in "connectedCallback()".
   * @param element - Theme Provider's "host" element
   * @param dataStore - object containing Theme Provider's current theme
   */
  setThemeProvider = (
    element: HTMLFluidThemeProviderElement,
    dataStore?: FluidKeyedObject<any> & { theme: FluidTheme }
  ): void => {
    this.themeProviderElement = element;
    this.themeProviderState$.next({ element, dataStore });
  };
}

export const ElementRegistry = new FluidElementRegistry();

// ================================================================ //
// -- Element Registry Utilities (Private)

/**
 * Updates "domRect" & "offsetTop" properties of all Element Registry
 * entries.
 * @param registry - FluidElementRegistry.registry
 */
const updateRegistry = (
  registry: FluidKeyedObject<FluidElementState>
): void => {
  const scrollOffset = window.pageYOffset;
  Object.values(registry).forEach((state) => {
    const rect = state.element.getBoundingClientRect();
    state.domRect = rect;
    state.offsetTop = rect.top + scrollOffset;
  });
};
